/**
 * Pure aggregation functions that operate on the lean records produced by
 * fetchRegistrationAnalytics.js.  All functions are synchronous and side-effect
 * free — safe to call inside useMemo.
 */

// ---------------------------------------------------------------------------
// Normalisation helpers
// ---------------------------------------------------------------------------

export function normalizeSchool(name) {
  return (name ?? "").trim().toLowerCase();
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/**
 * Apply up to five filters to an array of lean records.
 *
 * @param {object[]} records
 * @param {{
 *   school?: string,          // "" or "all" = no filter
 *   participationType?: string,
 *   grade?: string,
 *   competitionSchool?: string,
 *   from?: Date | null,
 *   to?: Date | null,
 * }} filters
 * @returns {object[]}
 */
function recordSchoolKey(r) {
  return r.schoolKey ?? (normalizeSchool(r.schoolName) || "(unknown)");
}

export function filterRecords(records, filters = {}) {
  const {
    school,
    participationType,
    grade,
    competitionSchool,
    from,
    to,
  } = filters;

  return records.filter((r) => {
    if (school && school !== "all") {
      if (recordSchoolKey(r) !== school) return false;
    }
    if (participationType && participationType !== "all") {
      if ((r.participationType ?? "").toLowerCase() !== participationType) return false;
    }
    if (grade && grade !== "all") {
      if (String(r.grade) !== String(grade)) return false;
    }
    if (competitionSchool && competitionSchool !== "all") {
      if ((r.competitionSchool ?? "") !== competitionSchool) return false;
    }
    if (from) {
      if (!r.at || r.at < from) return false;
    }
    if (to) {
      if (!r.at || r.at > to) return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// School leaderboard
// ---------------------------------------------------------------------------

/**
 * Build a ranked list of schools sorted by registration count descending.
 *
 * @param {object[]} records  – full lean record set (all competitions)
 * @param {number} [topN]     – if provided, truncate to top N
 * @returns {{ rank, displayName, key, count, pct, bySchool }[]}
 */
export function schoolLeaderboard(records, topN) {
  const countMap = {}; // normalizedKey -> { displayName, count, byHostingSchool }
  const total = records.length;

  for (const r of records) {
    const key = recordSchoolKey(r);
    if (!countMap[key]) {
      countMap[key] = {
        displayName: r.schoolDisplay ?? r.schoolName ?? "(unknown)",
        count: 0,
        byHostingSchool: {},
      };
    }
    countMap[key].count += 1;

    const hs = r.competitionSchool ?? "Unknown";
    countMap[key].byHostingSchool[hs] =
      (countMap[key].byHostingSchool[hs] ?? 0) + 1;
  }

  const sorted = Object.entries(countMap)
    .sort(([, a], [, b]) => b.count - a.count)
    .map(([key, v], i) => ({
      rank: i + 1,
      key,
      displayName: v.displayName,
      count: v.count,
      pct: total > 0 ? ((v.count / total) * 100).toFixed(1) : "0.0",
      byHostingSchool: v.byHostingSchool,
    }));

  return topN ? sorted.slice(0, topN) : sorted;
}

// ---------------------------------------------------------------------------
// Time series
// ---------------------------------------------------------------------------

/** Local calendar date key YYYY-MM-DD (avoids UTC off-by-one skips). */
export function toDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Monday-based week key for local date. */
export function toWeekKey(d) {
  const local = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = local.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  local.setDate(local.getDate() + diff);
  const y = local.getFullYear();
  const start = new Date(y, 0, 1);
  const week = Math.ceil(
    ((local - start) / 86400000 + start.getDay() + 1) / 7
  );
  return `${y}-W${String(week).padStart(2, "0")}`;
}

function parseWeekKey(key) {
  const [y, w] = key.split("-W");
  const jan4 = new Date(Number(y), 0, 4);
  const day = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - day + 1 + (Number(w) - 1) * 7);
  return monday;
}

function fillDayRange(buckets, from, to) {
  const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  while (cur <= end) {
    const k = toDateKey(cur);
    if (buckets[k] === undefined) buckets[k] = 0;
    cur.setDate(cur.getDate() + 1);
  }
}

function fillWeekRange(buckets, from, to) {
  const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const day = cur.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  cur.setDate(cur.getDate() + diff);
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  while (cur <= end) {
    const k = toWeekKey(cur);
    if (buckets[k] === undefined) buckets[k] = 0;
    cur.setDate(cur.getDate() + 7);
  }
}

/**
 * Build a time-series with every bucket in range (zeros included).
 * `from` and `to` are required for consistent charts.
 */
export function timeSeries(records, granularity = "day", from, to) {
  if (!from || !to) return [];

  const buckets = {};

  for (const r of records) {
    if (!r.at) continue;
    if (r.at < from || r.at > to) continue;
    const key = granularity === "week" ? toWeekKey(r.at) : toDateKey(r.at);
    buckets[key] = (buckets[key] ?? 0) + 1;
  }

  if (granularity === "week") {
    fillWeekRange(buckets, from, to);
  } else {
    fillDayRange(buckets, from, to);
  }

  return Object.entries(buckets)
    .sort(([a], [b]) => {
      if (granularity === "week") {
        return parseWeekKey(a) - parseWeekKey(b);
      }
      return a < b ? -1 : a > b ? 1 : 0;
    })
    .map(([key, count]) => ({ key, count }));
}

// ---------------------------------------------------------------------------
// Competition-level KPIs
// ---------------------------------------------------------------------------

/**
 * Compute headline KPIs for a set of (optionally filtered) competition records.
 *
 * @param {object[]} records
 * @returns {{
 *   total: number,
 *   interCount: number,
 *   intraCount: number,
 *   uniqueSchools: number,
 *   byGrade: Record<string,number>,
 *   bySchool: { displayName:string, count:number }[],
 * }}
 */
export function competitionKPIs(records) {
  let interCount = 0;
  let intraCount = 0;
  const schoolCounts = {};
  const byGrade = {};

  for (const r of records) {
    if (r.participationType === "inter") interCount++;
    else if (r.participationType === "intra") intraCount++;

    const key = recordSchoolKey(r);
    if (!schoolCounts[key]) {
      schoolCounts[key] = {
        displayName: r.schoolDisplay ?? r.schoolName ?? "(unknown)",
        count: 0,
        schoolKey: key,
      };
    }
    schoolCounts[key].count++;

    if (r.grade) {
      byGrade[r.grade] = (byGrade[r.grade] ?? 0) + 1;
    }
  }

  const bySchool = Object.values(schoolCounts).sort(
    (a, b) => b.count - a.count
  );

  return {
    total: records.length,
    interCount,
    intraCount,
    uniqueSchools: Object.keys(schoolCounts).length,
    byGrade,
    bySchool,
  };
}

// ---------------------------------------------------------------------------
// Overview KPIs (derived from collection-count map or lean records)
// ---------------------------------------------------------------------------

/**
 * Total count across all competition collections.
 * Accepts either the counts map from fetchCollectionCounts or lean records.
 *
 * @param {Record<string,number>} countsMap
 */
export function overviewTotals(countsMap) {
  return Object.values(countsMap).reduce((s, v) => s + v, 0);
}

/**
 * Per-competition-title registration count derived from lean records.
 *
 * @param {object[]} records
 * @returns {Record<string,number>}
 */
export function countByCompetition(records) {
  const out = {};
  for (const r of records) {
    out[r.competitionTitle] = (out[r.competitionTitle] ?? 0) + 1;
  }
  return out;
}

/**
 * Derive the distinct school names present in a set of records.
 *
 * @param {object[]} records
 * @returns {string[]}  sorted alphabetically
 */
/**
 * Canonical schools for filter dropdowns: { schoolKey, displayName }[]
 */
export function distinctSchools(records) {
  const map = new Map();
  for (const r of records) {
    const key = recordSchoolKey(r);
    if (!key || map.has(key)) continue;
    map.set(key, {
      schoolKey: key,
      displayName: r.schoolDisplay ?? r.schoolName ?? key,
    });
  }
  return Array.from(map.values()).sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );
}
